/**
 * 本机凭据库。token / simulationToken / connectData（连接数据工程级登录）/ AI Key 只放 localStorage，
 * 不进 .f3p、不进草稿 JSON、不随导出文件分发。
 *
 * connectData 段按 `project.meta.id` 分桶（docs/05-连接数据.md §2 / §4.5）：
 *   password / token / expiresAtMs / refreshedAtMs —— 工程级「项目连接」登录的密钥与票据。
 */
import type { ConnectDataSecrets } from '@/types/connectData'

const SECRETS_KEY = 'factory3d_local_secrets'

export type ProjectLocalSecrets = {
  token?: string
  simulationToken?: string
  connectData?: ConnectDataSecrets
}

type SecretsFile = Record<string, ProjectLocalSecrets>

// 与 ai/storage.ts 一致：Base64 简单混淆（防肩窥/防 localStorage 面板一览无余），不是加密。
// 密码/token 可能含中文等非 ASCII 字符，用 TextEncoder 走字节层，避免 btoa 直接吃非 Latin1 字符串报错。
function toBase64(str: string): string {
  return btoa(String.fromCharCode(...new TextEncoder().encode(str)))
}

function fromBase64(str: string): string {
  return new TextDecoder().decode(Uint8Array.from(atob(str), (c) => c.charCodeAt(0)))
}

function readAll(): SecretsFile {
  try {
    const raw = localStorage.getItem(SECRETS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(fromBase64(raw)) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    return parsed as SecretsFile
  } catch (err) {
    console.debug('[localSecrets] 凭据库解析失败，已回退为空', err)
    return {}
  }
}

function writeAll(file: SecretsFile) {
  localStorage.setItem(SECRETS_KEY, toBase64(JSON.stringify(file)))
}

function trimSecret(value: string | undefined): string | undefined {
  const t = value?.trim()
  return t ? t : undefined
}

function normalizeConnectData(value: unknown): ConnectDataSecrets | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const o = value as Record<string, unknown>
  const password = typeof o.password === 'string' ? o.password.trim() : ''
  const token = typeof o.token === 'string' ? o.token.trim() : ''
  const expiresAtMs = typeof o.expiresAtMs === 'number' ? o.expiresAtMs : undefined
  const refreshedAtMs = typeof o.refreshedAtMs === 'number' ? o.refreshedAtMs : undefined
  const authProfileKey = typeof o.authProfileKey === 'string' ? o.authProfileKey : undefined
  if (!password && !token && expiresAtMs == null && refreshedAtMs == null && !authProfileKey) return undefined
  return {
    ...(password ? { password } : {}),
    ...(token ? { token } : {}),
    ...(expiresAtMs != null ? { expiresAtMs } : {}),
    ...(refreshedAtMs != null ? { refreshedAtMs } : {}),
    ...(authProfileKey ? { authProfileKey } : {}),
  }
}

export function loadProjectSecrets(projectId: string): ProjectLocalSecrets {
  if (!projectId) return {}
  const entry = readAll()[projectId]
  if (!entry) return {}
  return {
    token: trimSecret(entry.token),
    simulationToken: trimSecret(entry.simulationToken),
    connectData: normalizeConnectData(entry.connectData),
  }
}

export function saveProjectSecrets(projectId: string, secrets: ProjectLocalSecrets) {
  if (!projectId) return
  const file = readAll()
  const next: ProjectLocalSecrets = {
    token: trimSecret(secrets.token),
    simulationToken: trimSecret(secrets.simulationToken),
    connectData: normalizeConnectData(secrets.connectData),
  }
  if (!next.token && !next.simulationToken && !next.connectData) {
    delete file[projectId]
  } else {
    file[projectId] = next
  }
  writeAll(file)
}

export function pickRealtimeSecrets(cfg: {
  token?: string
  simulationToken?: string
}): ProjectLocalSecrets {
  return {
    token: trimSecret(cfg.token),
    simulationToken: trimSecret(cfg.simulationToken),
  }
}

export function applyProjectSecrets<T extends { token?: string; simulationToken?: string }>(
  cfg: T,
  secrets: ProjectLocalSecrets,
): T {
  return {
    ...cfg,
    token: secrets.token ?? '',
    simulationToken: secrets.simulationToken ?? '',
  }
}

/** 读工程级「项目连接」的本机密钥（connectData 段）；没有则 undefined。 */
export function loadConnectDataSecrets(projectId: string): ConnectDataSecrets | undefined {
  return loadProjectSecrets(projectId).connectData
}

/** 写工程级「项目连接」的本机密钥（connectData 段）；保留 token/simulationToken 原有值。 */
export function saveConnectDataSecrets(projectId: string, secrets: ConnectDataSecrets): void {
  if (!projectId) return
  const file = readAll()
  const existing = file[projectId]
  const nextConnectData = normalizeConnectData({
    ...(existing?.connectData ?? {}),
    ...secrets,
  })
  const next: ProjectLocalSecrets = {
    token: trimSecret(existing?.token),
    simulationToken: trimSecret(existing?.simulationToken),
    connectData: nextConnectData,
  }
  if (!next.token && !next.simulationToken && !next.connectData) {
    delete file[projectId]
  } else {
    file[projectId] = next
  }
  writeAll(file)
}

/** 仅清除 connectData 登录票据（地址/账号/组织变更时）；保留密码与其它段。 */
export function clearConnectDataToken(projectId: string): void {
  if (!projectId) return
  const file = readAll()
  const existing = file[projectId]
  const password = existing?.connectData?.password
  const nextConnectData = password ? normalizeConnectData({ password }) : undefined
  const next: ProjectLocalSecrets = {
    token: trimSecret(existing?.token),
    simulationToken: trimSecret(existing?.simulationToken),
    connectData: nextConnectData,
  }
  if (!next.token && !next.simulationToken && !next.connectData) {
    delete file[projectId]
  } else {
    file[projectId] = next
  }
  writeAll(file)
}

/** 清除工程级「项目连接」的密钥（登出时调用），保留其它段。 */
export function clearConnectDataSecrets(projectId: string): void {
  if (!projectId) return
  const file = readAll()
  const existing = file[projectId]
  if (!existing) return
  const next: ProjectLocalSecrets = {
    token: trimSecret(existing.token),
    simulationToken: trimSecret(existing.simulationToken),
  }
  if (!next.token && !next.simulationToken) {
    delete file[projectId]
  } else {
    file[projectId] = next
  }
  writeAll(file)
}
